import { ab } from "../theme"

// Column ratios for contentpageTwoColumn (DEV-797), keyed "<image>-<text>" in
// percent. Every grid track is spelled out literally — nothing is assembled
// from the ratio string — so the map is the single, greppable source.
//
// - imageLeft / imageRight: grid-template-columns for either image side.
//   The text track never drops below 24ch so a 17–30 % column stays readable.
// - stackBelow: breakpoint below which the columns stack. Ratios with a column
//   under 25 % stack one breakpoint later, at lg instead of md.
// - imageSizes / gatsbySizes: responsive image hints for ResponsiveImage
//   (Storyblok CDN URL) and GatsbyImage (downloaded asset).
//
// "50-50" is the legacy layout: flex 1/1 with a 64px text padding equals
// 1fr 1fr with a 64px column gap, so existing content renders unchanged.
export const DEFAULT_RATIO = "50-50"

export const COLUMN_RATIOS = {
  "50-50": {
    imageLeft: "1fr 1fr",
    imageRight: "1fr 1fr",
    stackBelow: "md",
    imageSizes: ["100vw", "100vw", "50vw", "50vw"],
    gatsbySizes: "(min-width: 824px) 616px, 100vw",
  },
  "30-70": {
    imageLeft: "minmax(0,3fr) minmax(24ch,7fr)",
    imageRight: "minmax(24ch,7fr) minmax(0,3fr)",
    stackBelow: "md",
    imageSizes: ["100vw", "100vw", "30vw", "30vw"],
    gatsbySizes: "(min-width: 824px) 30vw, 100vw",
  },
  "70-30": {
    imageLeft: "minmax(0,7fr) minmax(24ch,3fr)",
    imageRight: "minmax(24ch,3fr) minmax(0,7fr)",
    stackBelow: "md",
    imageSizes: ["100vw", "100vw", "70vw", "70vw"],
    gatsbySizes: "(min-width: 824px) 70vw, 100vw",
  },
  "20-80": {
    imageLeft: "minmax(0,1fr) minmax(24ch,4fr)",
    imageRight: "minmax(24ch,4fr) minmax(0,1fr)",
    stackBelow: "lg",
    imageSizes: ["100vw", "100vw", "100vw", "20vw"],
    gatsbySizes: "(min-width: 1025px) 20vw, 100vw",
  },
  "80-20": {
    imageLeft: "minmax(0,4fr) minmax(24ch,1fr)",
    imageRight: "minmax(24ch,1fr) minmax(0,4fr)",
    stackBelow: "lg",
    imageSizes: ["100vw", "100vw", "100vw", "80vw"],
    gatsbySizes: "(min-width: 1025px) 80vw, 100vw",
  },
  "33-67": {
    imageLeft: "minmax(0,1fr) minmax(24ch,2fr)",
    imageRight: "minmax(24ch,2fr) minmax(0,1fr)",
    stackBelow: "md",
    imageSizes: ["100vw", "100vw", "33vw", "33vw"],
    gatsbySizes: "(min-width: 824px) 33vw, 100vw",
  },
  "67-33": {
    imageLeft: "minmax(0,2fr) minmax(24ch,1fr)",
    imageRight: "minmax(24ch,1fr) minmax(0,2fr)",
    stackBelow: "md",
    imageSizes: ["100vw", "100vw", "67vw", "67vw"],
    gatsbySizes: "(min-width: 824px) 67vw, 100vw",
  },
  "25-75": {
    imageLeft: "minmax(0,1fr) minmax(24ch,3fr)",
    imageRight: "minmax(24ch,3fr) minmax(0,1fr)",
    stackBelow: "md",
    imageSizes: ["100vw", "100vw", "25vw", "25vw"],
    gatsbySizes: "(min-width: 824px) 25vw, 100vw",
  },
  "75-25": {
    imageLeft: "minmax(0,3fr) minmax(24ch,1fr)",
    imageRight: "minmax(24ch,1fr) minmax(0,3fr)",
    stackBelow: "md",
    imageSizes: ["100vw", "100vw", "75vw", "75vw"],
    gatsbySizes: "(min-width: 824px) 75vw, 100vw",
  },
  "17-83": {
    imageLeft: "minmax(0,1fr) minmax(24ch,5fr)",
    imageRight: "minmax(24ch,5fr) minmax(0,1fr)",
    stackBelow: "lg",
    imageSizes: ["100vw", "100vw", "100vw", "17vw"],
    gatsbySizes: "(min-width: 1025px) 17vw, 100vw",
  },
  "83-17": {
    imageLeft: "minmax(0,5fr) minmax(24ch,1fr)",
    imageRight: "minmax(24ch,1fr) minmax(0,5fr)",
    stackBelow: "lg",
    imageSizes: ["100vw", "100vw", "100vw", "83vw"],
    gatsbySizes: "(min-width: 1025px) 83vw, 100vw",
  },
}

// Resolves the sx for the grid container and both columns. The image stays
// first in the DOM; its side and the mobile stacking order are grid placement.
export const twoColumnLayout = ({ ratio, reverse, stackOrder, inMiddle }) => {
  const isDefault = !COLUMN_RATIOS[ratio] || ratio === DEFAULT_RATIO
  const columns = isDefault
    ? COLUMN_RATIOS[DEFAULT_RATIO]
    : COLUMN_RATIOS[ratio]
  const bp = columns.stackBelow
  const textFirst = stackOrder === "text-first"

  return {
    columns,
    // The stacked gap between image and text sits on whichever side of the
    // image faces the text.
    imageStyle: {
      width: "100%",
      marginBottom: ab({ _: textFirst ? 0 : 3, [bp]: 0 }),
      ...(textFirst ? { marginTop: ab({ _: 3, [bp]: 0 }) } : {}),
    },
    // Wide diagrams must stay whole in image-heavy ratios; the default keeps
    // the Image component's own fit so legacy output does not change.
    imageObjectFit: isDefault ? undefined : "contain",
    container: {
      display: "grid",
      gridTemplateColumns: ab({
        _: "1fr",
        [bp]: reverse ? columns.imageRight : columns.imageLeft,
      }),
      columnGap: ab({ _: 0, [bp]: 5 }),
    },
    imageColumn: {
      gridColumn: ab({ _: 1, [bp]: reverse ? 2 : 1 }),
      gridRow: ab({ _: textFirst ? 2 : 1, [bp]: 1 }),
      minWidth: 0,
    },
    textColumn: {
      gridColumn: ab({ _: 1, [bp]: reverse ? 1 : 2 }),
      gridRow: ab({ _: textFirst ? 1 : 2, [bp]: 1 }),
      // Legacy flex column: alignSelf centered horizontally when stacked and
      // vertically side by side; grid needs both axes spelled out.
      alignSelf: inMiddle ? ab({ [bp]: "center" }) : undefined,
      justifySelf: inMiddle ? ab({ _: "center", [bp]: "stretch" }) : undefined,
    },
  }
}
